/**
 * A picture behind a page.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * **To add a fit or a strength, add a row to the table it belongs to.** The
 * type, the list of ids and every picker follow from it.
 * ─────────────────────────────────────────────────────────────────────────
 *
 * It lives with the layout rather than with the page break that currently
 * holds one, because it describes the sheet rather than the section: what a
 * page looks like, not what it says. A cover, or a document-wide default,
 * would want exactly this shape, and would want it read exactly this way.
 *
 * **It covers the whole sheet, margins included.** That is the point of it: a
 * background held inside the margins is a panel, and a panel is a thing you
 * draw on the page rather than a thing the page is made of.
 *
 * **Strength is a fixed set rather than a slider.** A background is behind
 * text that has to stay readable, and the number that matters is not "what
 * looks nice at 100% zoom" but "what survives being printed on an office
 * machine and photocopied". Three named strengths make that a choice between
 * outcomes; a percentage makes it a fiddle that ends at 43% because somebody
 * let go of the mouse.
 *
 * Pure. Names and numbers, no rendering.
 */

/** How the picture is sized against the sheet. */
export const BACKGROUND_FITS = {
  /** Filled, cropping whatever does not fit. The usual choice for a photograph. */
  cover: { size: 'cover', repeat: 'no-repeat' },
  /** Whole picture, letterboxed. For a mark or a diagram that must not be cut. */
  contain: { size: 'contain', repeat: 'no-repeat' },
  /** Stretched to the sheet, ignoring its proportions. For a wash or a gradient. */
  stretch: { size: '100% 100%', repeat: 'no-repeat' },
  /** Repeated at its own size. For a texture or a watermark. */
  tile: { size: 'auto', repeat: 'repeat' },
} as const

export type BackgroundFit = keyof typeof BACKGROUND_FITS

export const BACKGROUND_FIT_IDS = Object.keys(BACKGROUND_FITS) as readonly BackgroundFit[]

/**
 * How strongly it shows through.
 *
 * `full` is for a divider or a cover, where nothing has to be read over it.
 * `soft` and `faint` are for a page carrying text, and faint is the one that
 * still works when the report is photocopied.
 */
export const BACKGROUND_STRENGTHS = {
  full: '1',
  soft: '0.35',
  faint: '0.12',
} as const

export type BackgroundStrength = keyof typeof BACKGROUND_STRENGTHS

export const BACKGROUND_STRENGTH_IDS = Object.keys(
  BACKGROUND_STRENGTHS,
) as readonly BackgroundStrength[]

/** A picture behind a page, and how it sits there. */
export interface PageBackground {
  /** The image as a data URL, or empty for none. */
  readonly image: string
  readonly fit: BackgroundFit
  readonly strength: BackgroundStrength
}

export const NO_BACKGROUND: PageBackground = {
  image: '',
  fit: 'cover',
  strength: 'full',
}

/** Whether there is a picture to draw. */
export function hasBackground(background: PageBackground): boolean {
  return background.image !== ''
}

/**
 * Only a data URL naming an image type.
 *
 * This value reaches a stylesheet as a `url()`, and it came off a document
 * that somebody else may have written. A `javascript:` or a remote address
 * accepted here would be a stored document deciding what this page fetches,
 * or runs. Nothing but an inline image is a legitimate value, so nothing else
 * is kept.
 */
function readImage(value: unknown): string {
  return typeof value === 'string' && /^data:image\/[a-z0-9.+-]+;base64,[a-z0-9+/=]+$/i.test(value)
    ? value
    : ''
}

function oneOf<T extends string>(options: readonly T[], value: unknown, fallback: T): T {
  return typeof value === 'string' && (options as readonly string[]).includes(value)
    ? (value as T)
    : fallback
}

/**
 * A background read from a stored document.
 *
 * Forgiving in every field, like everything else read off a document: a page
 * saved by a version offering a fit this one does not opens with a fit it
 * does, rather than not opening.
 */
export function readBackground(value: unknown): PageBackground {
  const source = (typeof value === 'object' && value !== null ? value : {}) as Record<
    string,
    unknown
  >

  return {
    image: readImage(source['image']),
    fit: oneOf(BACKGROUND_FIT_IDS, source['fit'], NO_BACKGROUND.fit),
    strength: oneOf(BACKGROUND_STRENGTH_IDS, source['strength'], NO_BACKGROUND.strength),
  }
}

/**
 * What the sheet should be told, for a page carrying this background.
 *
 * Returns nothing at all when there is no picture, rather than an empty
 * `url()`. A page without a background then adds no custom properties and
 * inherits whatever is above it, which is what a document-wide default would
 * need in order to show through.
 */
export function backgroundStyle(background: PageBackground): Record<string, string> {
  if (!hasBackground(background)) return {}

  const fit = BACKGROUND_FITS[background.fit]

  return {
    // Quoted, because a data URL contains characters CSS would otherwise read
    // as the end of the value.
    '--page-bg-image': `url("${background.image}")`,
    '--page-bg-size': fit.size,
    '--page-bg-repeat': fit.repeat,
    '--page-bg-strength': BACKGROUND_STRENGTHS[background.strength],
  }
}
